import { useState, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ExternalLink, BarChart3, Info, Copy, Check, Loader2 } from "lucide-react";
import { DASHBOARDS, findDashboardBySlug, embedUrl, viewUrl } from "@/lib/dashboards";

export default function Statistik() {
  const { kode } = useParams();
  // Support: /statistik OR /statistik/:kode where kode is slug (pilahsar/tangkap/kelautan)
  const dash = kode ? findDashboardBySlug(kode) : DASHBOARDS.PILAHSAR;
  const [showEmbed, setShowEmbed] = useState(true);
  const [copied, setCopied] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  useEffect(() => {
    setIframeLoaded(false);
  }, [dash?.slug]);

  if (kode && !dash) return <Navigate to="/statistik/pilahsar" replace />;

  const VIEW_URL = viewUrl(dash);
  const EMBED_URL = embedUrl(dash);

  const copyUrl = () => {
    navigator.clipboard.writeText(VIEW_URL);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const otherDashboards = Object.values(DASHBOARDS).filter((d) => d.kode !== dash.kode);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#0086C6] font-semibold mb-2">Data Interaktif</div>
          <h1 className="font-heading font-bold text-3xl lg:text-5xl text-[#003A5C]">{dash.title}</h1>
          <p className="text-slate-600 mt-3 max-w-3xl">
            Statistik langsung dari Google Looker Studio DKP Provinsi Jawa Barat — <strong>{dash.subtitle}</strong>.
          </p>
        </div>
        <a href={VIEW_URL} target="_blank" rel="noopener noreferrer" data-testid="statistik-open-external"
           className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#00B4D8] hover:bg-[#48CAE4] text-[#003A5C] font-bold text-sm transition-colors">
          Buka di Tab Baru <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      {/* Dashboard switcher */}
      <div className="mb-6 flex flex-wrap gap-2">
        {Object.values(DASHBOARDS).map((d) => (
          <Link key={d.kode} to={`/statistik/${d.slug}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition-colors ${
              d.kode === dash.kode
                ? "bg-[#003A5C] text-white border-[#003A5C]"
                : "bg-white text-[#003A5C] border-slate-200 hover:border-[#00B4D8]"
            }`}>
            {d.label}
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
          <div className="w-10 h-10 rounded-full bg-[#E1F5FF] grid place-items-center mb-3"><BarChart3 className="w-5 h-5 text-[#005F8E]" /></div>
          <div className="font-heading font-bold text-[#003A5C] text-sm">Sumber Resmi</div>
          <div className="text-xs text-slate-500 mt-1">Data dari Bidang {dash.label} DKP Jabar</div>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
          <div className="w-10 h-10 rounded-full bg-[#E1F5FF] grid place-items-center mb-3"><BarChart3 className="w-5 h-5 text-[#005F8E]" /></div>
          <div className="font-heading font-bold text-[#003A5C] text-sm">Update Otomatis</div>
          <div className="text-xs text-slate-500 mt-1">Menampilkan data terkini secara realtime</div>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
          <div className="w-10 h-10 rounded-full bg-[#E1F5FF] grid place-items-center mb-3"><BarChart3 className="w-5 h-5 text-[#005F8E]" /></div>
          <div className="font-heading font-bold text-[#003A5C] text-sm">Interaktif</div>
          <div className="text-xs text-slate-500 mt-1">Filter &amp; drill-down langsung di dashboard</div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Pratinjau Dashboard</div>
        <div className="flex items-center gap-2">
          <button onClick={copyUrl} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold transition-colors">
            {copied ? <><Check className="w-3.5 h-3.5" /> Tersalin</> : <><Copy className="w-3.5 h-3.5" /> Salin URL</>}
          </button>
          <button onClick={() => setShowEmbed((v) => !v)} className="text-xs px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold transition-colors">
            {showEmbed ? "Sembunyikan Embed" : "Tampilkan Embed"}
          </button>
        </div>
      </div>

      {showEmbed && (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-lg overflow-hidden">
          <div className="bg-[#003A5C] text-white px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-white/10 grid place-items-center"><BarChart3 className="w-4 h-4" /></div>
              <div>
                <div className="font-heading font-bold text-sm">{dash.title.toUpperCase()}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#48CAE4]">Powered by Google Looker Studio</div>
              </div>
            </div>
            <a href={VIEW_URL} target="_blank" rel="noopener noreferrer" className="text-[#48CAE4] hover:text-white text-xs inline-flex items-center gap-1">
              Sumber <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <div className="relative">
            {!iframeLoaded && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-slate-50">
                <Loader2 className="w-8 h-8 text-[#005F8E] animate-spin" />
                <div className="text-sm text-slate-500">Memuat dashboard...</div>
              </div>
            )}
            <iframe
              title={dash.title}
              src={EMBED_URL}
              className="w-full bg-slate-50"
              style={{ height: "1200px", border: 0 }}
              allowFullScreen
              onLoad={() => setIframeLoaded(true)}
              data-testid="datastudio-iframe"
            />
          </div>
        </div>
      )}

      <div className="mt-6 text-xs text-slate-500 flex items-start gap-2">
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        <p>
          Lihat bidang terkait:{" "}
          <Link to={`/bidang/${dash.bidangSlug}`} className="text-[#005F8E] hover:underline">Bidang {dash.label}</Link>.
          Sumber:{" "}
          <a href={VIEW_URL} target="_blank" rel="noopener noreferrer" className="text-[#005F8E] hover:underline">datastudio.google.com</a>.
        </p>
      </div>

      {otherDashboards.length > 0 && (
        <div className="mt-10">
          <div className="text-xs uppercase tracking-widest text-[#0086C6] font-semibold mb-3">Dashboard Bidang Lain</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {otherDashboards.map((d) => (
              <Link key={d.kode} to={`/statistik/${d.slug}`} className="lift-card bg-white rounded-2xl p-4 border border-slate-100 flex items-center gap-4 hover:border-[#00B4D8]">
                <div className="w-11 h-11 rounded-xl bg-[#E1F5FF] grid place-items-center"><BarChart3 className="w-5 h-5 text-[#005F8E]" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-[#003A5C] text-sm">{d.title}</div>
                  <div className="text-xs text-slate-500 truncate">{d.subtitle}</div>
                </div>
                <ExternalLink className="w-4 h-4 text-slate-400" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
